import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import CommissionDialog from "../components/shared/CommissionDialog";
import { BidUpDialog, BoostConfirmDialog } from "./commissions/CommissionDialogs";
import { WantedCard, PostedCard, WonCard } from "./commissions/CommissionCards";

// Great-circle distance between two lat/lng pairs. Returns km, or null if
// any coordinate is missing. Keeps the wanted-list sort defensive against
// commissions that drift in without geo metadata.
const _kmBetween = (lat1, lng1, lat2, lng2) => {
  if ([lat1, lng1, lat2, lng2].some((v) => typeof v !== "number")) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Commissions Hub — `/commissions`
// Three tabs: posted (EP view) · wanted (producer feed near-me) · won (producer deliveries).
// Posted shows status pills + Cancel button on open offers + link to the resulting video.
// Wanted lists open offers, sorted by distance from the user's home_city when set.
const CommissionsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("wanted"); // wanted | posted | won
  const [posted, setPosted] = useState([]);
  const [open, setOpen] = useState([]);
  // Drives the in-page "Commission a Video" CTA → CommissionDialog.
  // Was previously only reachable via the floating FAB, which was easy
  // to miss on mobile.
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [won, setWon] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [bumpDialog, setBumpDialog] = useState(null); // { id, title, currentBounty } | null
  const [bumpAmount, setBumpAmount] = useState(5);
  const [bumping, setBumping] = useState(false);
  const [boostingId, setBoostingId] = useState(null);
  const [boostConfirmId, setBoostConfirmId] = useState(null);
  // EP user IDs with a ≥3-boost streak in the last 7 days — used for the 🔥 hot badge.
  const [hotEpIds, setHotEpIds] = useState(new Set());

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const openPromise = api.get(`/commissions/open`);
      const hotPromise = api.get(`/commissions/hot-eps`).catch(() => ({ data: { ep_user_ids: [] } }));

      if (user && token) {
        const [pRes, oRes, wRes, hRes] = await Promise.all([
          api.get(`/commissions/mine/posted`),
          openPromise,
          api.get(`/commissions/mine/won`),
          hotPromise,
        ]);
        setPosted(pRes.data || []);
        setOpen(oRes.data || []);
        setWon(wRes.data || []);
        setHotEpIds(new Set(hRes.data?.ep_user_ids || []));
      } else {
        const [oRes, hRes] = await Promise.all([openPromise, hotPromise]);
        setPosted([]);
        setOpen(oRes.data || []);
        setWon([]);
        setHotEpIds(new Set(hRes.data?.ep_user_ids || []));
      }
    } catch (_err) {
      toast.error("Couldn't load commissions");
    }
    setLoading(false);
  }, [user, token]);

  useEffect(() => { reload(); }, [reload]);

  const handleCancel = async (cid) => {
    if (!window.confirm("Cancel this commission? Escrowed credits return (minus the 0.25 fee).")) return;
    setBusyId(cid);
    try {
      const res = await api.post(`/commissions/${cid}/cancel`, {});
      toast.success(`Refunded ${res.data.refunded.toFixed(2)} credits`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel commission");
    }
    setBusyId(null);
  };

  const handleAccept = async (cid) => {
    setAccepting(cid);
    try {
      await api.post(`/commissions/${cid}/lock`, {});
      navigate(`/dashboard?commission=${cid}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't accept commission");
    }
    setAccepting(null);
  };

  const handleBidUp = async () => {
    if (!bumpDialog) return;
    if (bumpAmount <= 0) { toast.error("Pick a bump amount"); return; }
    setBumping(true);
    try {
      const res = await api.post(`/commissions/${bumpDialog.id}/bid-up`,
        { additional_bounty: Number(bumpAmount) });
      toast.success(`Bumped! Bounty is now +${res.data.bounty_credits.toFixed(2)} cr`);
      setBumpDialog(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't bump this commission");
    }
    setBumping(false);
  };

  const confirmBoost = async () => {
    const cid = boostConfirmId;
    if (!cid) return;
    setBoostConfirmId(null);
    setBoostingId(cid);
    try {
      const res = await api.post(`/commissions/${cid}/boost`, {});
      toast.success(`Boosted! Top of feed for ${res.data.duration_hours}h.`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't boost this commission");
    }
    setBoostingId(null);
  };

  // Filter open commissions to the ones THIS user can accept (not their own),
  // and sort by distance from their home city when available. Logged-out
  // visitors see every open offer.
  const wantedList = React.useMemo(() => {
    const list = user ? open.filter((c) => c.ec_user_id !== user.id) : open;
    if (user?.home_city_lat && user?.home_city_lng) {
      return [...list].map((c) => ({
        ...c,
        _km: _kmBetween(user.home_city_lat, user.home_city_lng, c.location?.lat, c.location?.lng),
      })).sort((a, b) => {
        const ak = a._km ?? Number.POSITIVE_INFINITY;
        const bk = b._km ?? Number.POSITIVE_INFINITY;
        return ak - bk;
      });
    }
    return list;
  }, [open, user]);

  const counts = { wanted: wantedList.length, posted: posted.length, won: won.length };
  const openAuth = (detail) => window.dispatchEvent(new CustomEvent("open-auth", detail ? { detail } : undefined));
  const watchVideo = (vid) => navigate(`/video/${vid}`);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black py-6" data-testid="commissions-page">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="h-6 w-6 text-amber-400" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Commissions</h1>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          Post bounties for videos you wish existed, or accept open offers from other Exec Producers and earn credits.
        </p>

        {user ? (
          <button
            onClick={() => setShowCommissionDialog(true)}
            className="w-full sm:w-auto mb-6 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-gray-900 font-bold rounded-lg shadow-lg shadow-amber-500/20 transition-all"
            data-testid="commissions-cta-create"
          >
            <Sparkles className="h-5 w-5" /> Commission a Video
          </button>
        ) : (
          <button
            onClick={() => openAuth({ register: true })}
            className="w-full sm:w-auto mb-6 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-gray-900 font-bold rounded-lg shadow-lg shadow-amber-500/20 transition-all"
            data-testid="commissions-cta-signup"
          >
            <Sparkles className="h-5 w-5" /> Sign up to commission a video
          </button>
        )}

        {showCommissionDialog && (
          <CommissionDialog
            open={showCommissionDialog}
            onClose={() => { setShowCommissionDialog(false); reload && reload(); }}
          />
        )}

        {!user && (
          <div className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 border border-amber-500/30 rounded-lg p-4 mb-5" data-testid="commissions-guest-banner">
            <p className="text-amber-200 text-sm font-medium">
              You're browsing as a guest. {wantedList.length > 0 ? `${wantedList.length} open ${wantedList.length === 1 ? "commission" : "commissions"} right now —` : "Sign up free —"}
              {" "}
              <button
                onClick={() => openAuth()}
                className="text-emerald-300 hover:text-emerald-200 underline font-semibold"
                data-testid="commissions-guest-signup-link"
              >
                sign up free
              </button>
              {" "}to claim one and earn credits, or post your own.
            </p>
          </div>
        )}

        {user ? (
          <div className="grid grid-cols-3 gap-1 bg-gray-900/60 border border-gray-800 rounded-lg p-1 mb-5">
            {[
              { key: "wanted", label: "Wanted near me", count: counts.wanted, color: "amber" },
              { key: "posted", label: "I posted", count: counts.posted, color: "emerald" },
              { key: "won",    label: "My deliveries", count: counts.won, color: "cyan" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`text-xs sm:text-sm py-2 px-2 rounded-md transition-colors ${
                  tab === t.key
                    ? (t.color === "amber" ? "bg-amber-500 text-gray-900 font-semibold"
                      : t.color === "cyan" ? "bg-cyan-600 text-white font-semibold"
                      : "bg-emerald-600 text-white font-semibold")
                    : "text-gray-400 hover:text-gray-200 hover:bg-gray-800"
                }`}
                data-testid={`commissions-tab-${t.key}`}
              >
                {t.label} <span className="opacity-70 ml-1">({t.count})</span>
              </button>
            ))}
          </div>
        ) : null}

        {loading && <p className="text-gray-500 text-sm" data-testid="commissions-loading">Loading…</p>}

        {/* Wanted tab — always shown to guests; tabbed for authed users. */}
        {(!user || tab === "wanted") && !loading && (
          <div className="space-y-3" data-testid="commissions-wanted-list">
            {wantedList.length === 0 ? (
              <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-6 text-center text-gray-500 text-sm">
                Nothing open right now. Check back soon — or post your own commission.
              </div>
            ) : wantedList.map((c) => (
              <WantedCard
                key={c.id}
                c={c}
                user={user}
                hotEpIds={hotEpIds}
                accepting={accepting}
                onAccept={() => handleAccept(c.id)}
                onSignUp={() => openAuth()}
              />
            ))}
          </div>
        )}

        {/* Posted tab */}
        {tab === "posted" && !loading && (
          <div className="space-y-3" data-testid="commissions-posted-list">
            {posted.length === 0 ? (
              <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-6 text-center text-gray-500 text-sm">
                You haven't posted any commissions yet. Tap the gold "Commission" button anywhere in the app to post one.
              </div>
            ) : posted.map((c) => (
              <PostedCard
                key={c.id}
                c={c}
                busyId={busyId}
                boostingId={boostingId}
                onBump={() => { setBumpDialog({ id: c.id, title: c.title, currentBounty: c.bounty_credits || 0 }); setBumpAmount(5); }}
                onBoost={() => setBoostConfirmId(c.id)}
                onCancel={() => handleCancel(c.id)}
                onWatch={watchVideo}
              />
            ))}
          </div>
        )}

        {/* Won tab */}
        {tab === "won" && !loading && (
          <div className="space-y-3" data-testid="commissions-won-list">
            {won.length === 0 ? (
              <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-6 text-center text-gray-500 text-sm">
                You haven't fulfilled any commissions yet. Pop over to "Wanted near me" to claim one.
              </div>
            ) : won.map((c) => (
              <WonCard key={c.id} c={c} onWatch={watchVideo} />
            ))}
          </div>
        )}
      </div>

      <BidUpDialog
        target={bumpDialog}
        amount={bumpAmount}
        onAmount={setBumpAmount}
        busy={bumping}
        onSubmit={handleBidUp}
        onCancel={() => setBumpDialog(null)}
      />

      <BoostConfirmDialog
        open={!!boostConfirmId}
        onOpenChange={(o) => !o && setBoostConfirmId(null)}
        onConfirm={confirmBoost}
      />
    </div>
  );
};

export default CommissionsPage;
